import React from "react";
import Select, { StylesConfig } from "react-select";

interface Props {
  id: string;
  options: { value: string; label: string }[];
  onChange: (
    selected:
      | { value: string; label: string }
      | { value: string; label: string }[]
      | null
  ) => void;
  isMulti?: boolean;
  defaultValue?: { value: string; label: string }[];
  placeholder?: string;
  isClearable?: boolean;
  className?: string;
  classNamePrefix?: string;
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  isSearchable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  menuPlacement?: "auto" | "top" | "bottom";
  menuPosition?: "absolute" | "fixed";
  menuContainerStyle?: React.CSSProperties;
  menuContainerClassName?: string;
  styles?: StylesConfig<{ value: string; label: string }, boolean>;
  label?: string;
  error?: string;
  value?: string | { value: string; label: string } | null;
  name: string;
  labelAction?: React.ReactNode;
  isRequired?: boolean;
}

const CustomReactSelect = React.forwardRef<any, Props>(
  (
    {
      id,
      options,
      onChange,
      isMulti,
      defaultValue,
      placeholder,
      className,
      classNamePrefix,
      closeMenuOnSelect,
      hideSelectedOptions,
      isSearchable,
      isDisabled,
      isLoading,
      menuPlacement,
      menuPosition,
      styles,
      label,
      error,
      value,
      name,
      isClearable,
      labelAction,
      isRequired,
    },
    ref
  ) => {
    return (
      <div
        className={`flex flex-col min-w-[213px] ${
          isDisabled && "cursor-not-allowed"
        }`}
      >
        {label && (
          <div className="flex items-center justify-between">
            <label
              className="block BookEZ_font_body_size BookEZ_font_light_bold py-0.5"
              htmlFor={id}
              style={{
                color: error ? "var(--error_text)" : "var(--font_color_gray_2)",
              }}
            >
              {label}{" "}
              {isRequired && <span className="BookEZ_error_text">*</span>}
            </label>
            {labelAction && labelAction}
          </div>
        )}
        <Select
          ref={ref} // 👈 This is critical for setFocus to work
          inputId={id}
          name={name}
          options={options}
          isMulti={isMulti}
          className={className}
          classNamePrefix={classNamePrefix}
          closeMenuOnSelect={
            closeMenuOnSelect !== undefined ? closeMenuOnSelect : true
          }
          hideSelectedOptions={hideSelectedOptions}
          isSearchable={isSearchable !== undefined ? isSearchable : true}
          isDisabled={isDisabled}
          isLoading={isLoading}
          menuPlacement={menuPlacement || "auto"}
          menuPosition={menuPosition || "fixed"}
          menuPortalTarget={document.body} // Render menu to body
          blurInputOnSelect={false} // Prevent blur on select
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999, // Ensure high z-index
              pointerEvents: "auto", // Enable pointer events
            }),
            menu: (provided) => ({
              ...provided,
              zIndex: 9999, // High z-index
              position: "absolute",
              pointerEvents: "auto", // Enable pointer events
            }),
            menuList: (provided) => ({
              ...provided,
              paddingTop: 0,
              paddingBottom: 0,
              pointerEvents: "auto", // Enable pointer events
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected
                ? "#f3f4f6"
                : "var(--card_bg_color)",
              color: state.isSelected ? "black" : "var(--font_color_gray_2)",
              cursor: "pointer",
              pointerEvents: "auto", // Enable pointer events
              "&:hover": {
                backgroundColor: "#f3f4f6",
                color: "black",
              },
            }),
            control: (provided, state) => ({
              ...provided,
              borderColor: error
                ? "var(--error_border)"
                : "var(--card_border_color)",
              backgroundColor: state.isDisabled
                ? "var(--disabled_background)"
                : "var(--card_bg_color)",
              color: "var(--font_color_gray_2)",
              cursor: state.isDisabled ? "not-allowed" : "default",
              borderRadius: "var(--input_border_radius)",
              minHeight: "40px",
              fontSize: "var(--font_body_size)",
              "&:hover": {
                cursor: `${
                  state.isDisabled ? "not-allowed" : "default"
                } !important`,
                borderColor: error
                  ? "var(--error_border)"
                  : "var(--card_border_color)",
              },
            }),
            input: (provided, state) => ({
              ...provided,
              color: "var(--font_color_gray_2)",
              fontSize: "var(--font_body_size)",
              cursor: state.isDisabled ? "not-allowed" : "text",
            }),
            placeholder: (provided, state) => ({
              ...provided,
              cursor: state.isDisabled ? "not-allowed" : "default",
              fontSize: "var(--font_body_size)",
              color: "var(--font_color_gray_1)",
              // fontWeight : "var(--font_body_medium)",
            }),
            singleValue: (provided, state) => ({
              ...provided,
              color: "var(--font_color_gray_2)",
              fontSize: "var(--font_body_size)",
              cursor: state.isDisabled ? "not-allowed" : "default",
            }),
            multiValue: (provided, state) => ({
              ...provided,
              cursor: state.isDisabled ? "not-allowed" : "default",
            }),
            multiValueLabel: (provided, state) => ({
              ...provided,
              cursor: state.isDisabled ? "not-allowed" : "default",
            }),
            multiValueRemove: (provided, state) => ({
              ...provided,
              cursor: state.isDisabled ? "not-allowed" : "pointer",
              "&:hover": {
                cursor: state.isDisabled ? "not-allowed" : "pointer",
              },
            }),
            indicatorSeparator: (provided, state) => ({
              ...provided,
              cursor: state.isDisabled ? "not-allowed" : "default",
            }),
            dropdownIndicator: (provided, state) => ({
              ...provided,
              cursor: state.isDisabled ? "not-allowed" : "pointer",
              "&:hover": {
                cursor: state.isDisabled ? "not-allowed" : "pointer",
              },
            }),
            ...styles,
          }}
          placeholder={placeholder}
          isClearable={isClearable}
          onChange={(newValue) => {
            if (isMulti) {
              onChange(
                (newValue as { value: string; label: string }[] | null) ?? []
              );
            } else {
              onChange(
                (newValue as { value: string; label: string } | null) ?? null
              );
            }
          }}
          defaultValue={defaultValue}
          value={value as { value: string; label: string } | null}
        />
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  }
);

export default CustomReactSelect;
